import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';

/**
 * RotatingTicker Component
 * 
 * Compact horizontal rotating ticker banner (responsive).
 * Features:
 * - Responsive layout for mobile, tablet, and desktop
 * - Auto-rotates every 10 seconds
 * - Pauses on hover (desktop) or touch (mobile)
 * - Smooth horizontal sliding animation (left to right)
 * - Keyboard navigable
 * - ARIA accessible
 */
const RotatingTicker = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  // Card data with headlines, descriptions, CTAs, and icons
  const cards = [
    {
      headline: 'Build Smarter With Octalchip',
      description: 'Custom software, AI solutions, and automation for growing businesses.',
      cta: 'Talk to Experts',
      ctaMobile: 'Experts',
      icon: 'Rocket',
      action: () => {
        window.open('https://cal.com/octalchip/30min', '_blank');
      }
    },
    {
      headline: 'Custom Software Development',
      description: 'Web, mobile, and enterprise apps tailored to your business.',
      cta: 'View Solutions',
      ctaMobile: 'Solutions',
      icon: 'Code',
      action: () => {
        window.location.href = '/services';
      }
    },
    {
      headline: 'AI & Workflow Automation',
      description: 'Automate tasks using AI, n8n, Zapier, and Make.',
      cta: 'Automate My Process',
      ctaMobile: 'Automate',
      icon: 'Zap',
      action: () => {
        window.location.href = '/services/workflow-automation';
      }
    },
    {
      headline: 'Marketing Automation',
      description: 'WhatsApp, RCS, LinkedIn outreach, email & SMS, consent-first and measurable.',
      cta: 'Explore Marketing',
      ctaMobile: 'Marketing',
      icon: 'Megaphone',
      action: () => {
        window.location.href = '/services/marketing-services';
      }
    },
    {
      headline: 'Email Validation API',
      description: 'Reduce bounce rates with fast and accurate email verification.',
      cta: 'Try Mail Validator',
      ctaMobile: 'Try It',
      icon: 'Mail',
      action: () => {
        window.open('https://mailvalidator.octalchip.com/', '_blank');
      }
    },
    {
      headline: 'QuickSite Business Websites',
      description: 'Done-for-you sites with hosting, SSL, templates, and WhatsApp support.',
      cta: 'Get QuickSite',
      ctaMobile: 'QuickSite',
      icon: 'LayoutTemplate',
      action: () => {
        window.open('https://quicksite.octalchip.com/', '_blank');
      }
    },
    {
      headline: 'SaaS & Startup Development',
      description: 'From MVP to scalable SaaS products.',
      cta: 'Launch My SaaS',
      ctaMobile: 'Launch',
      icon: 'TrendingUp',
      action: () => {
        window.open('https://cal.com/octalchip/30min', '_blank');
      }
    },
    {
      headline: 'Cloud & DevOps Solutions',
      description: 'AWS deployment, scaling, security, and optimization.',
      cta: 'Optimize Cloud',
      ctaMobile: 'Optimize',
      icon: 'Cloud',
      action: () => {
        window.location.href = '/services/cloud-devops';
      }
    },
    {
      headline: 'API & System Integrations',
      description: 'Connect CRMs, payment gateways, and third-party tools.',
      cta: 'Integrate Systems',
      ctaMobile: 'Integrate',
      icon: 'Plug',
      action: () => {
        window.location.href = '/services';
      }
    },
    {
      headline: 'Reliable. Scalable. Secure.',
      description: 'Production-ready solutions built for performance.',
      cta: 'Why Octalchip?',
      ctaMobile: 'Learn More',
      icon: 'Shield',
      action: () => {
        window.location.href = '/about';
      }
    }
  ];

  // Auto-rotation logic
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
      }, 10000); // 10 seconds rotation interval
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, cards.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex((prevIndex) => (prevIndex - 1 + cards.length) % cards.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cards.length]);

  // Animation variants for horizontal sliding (left to right) - Enhanced for premium feel
  const cardVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 30 : -30, // Subtle slide in
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0, // Center position
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 30 : -30, // Subtle slide out
      opacity: 0,
      scale: 0.95
    })
  };

  // Smooth transition configuration - Premium easing
  const transition = {
    type: 'spring',
    stiffness: 300,
    damping: 30,
    mass: 0.8
  };

  const currentCard = cards[currentIndex];
  const direction = 1; // Always slide right (left to right) for horizontal rotation

  return (
    <section
      className="w-full bg-gradient-to-br from-[#00d690]/5 via-[#00d690]/8 to-[#00d690]/10 relative z-40 shadow-sm border-b border-[#00d690]/10 backdrop-blur-sm"
      aria-label="Rotating ticker"
      role="region"
    >
      {/* Subtle animated background pattern */}
      <div className="absolute inset-0 opacity-30 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-[#00d690]/5 to-transparent animate-pulse" />
      </div>

      <div
        className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
      >
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 py-2 sm:py-2.5 lg:py-3">
          {/* Left side - Icon and Headline */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                className="relative"
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-14 lg:h-14 bg-gradient-to-br from-[#00d690]/20 to-[#00d690]/10 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md border border-[#00d690]/20 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:border-[#00d690]/30">
                  {/* Animated glow effect */}
                  <div className="absolute inset-0 bg-[#00d690]/20 rounded-lg sm:rounded-xl blur-md opacity-0 hover:opacity-100 transition-opacity duration-300 -z-10" />
                  <Icon
                    name={currentCard.icon}
                    size={18}
                    className="sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-6 lg:h-6 text-[#00d690] drop-shadow-sm"
                  />
                </div>
              </motion.div>
            </div>
            
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={cardVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={transition}
                  className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-1.5 lg:gap-3"
                >
                  <h2 
                    className="text-sm sm:text-base md:text-lg lg:text-lg xl:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876] leading-tight"
                    style={{
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      wordBreak: 'break-word',
                      lineHeight: '1.2'
                    }}
                  >
                    {currentCard.headline}
                  </h2>
                  <span 
                    className="text-[10px] sm:text-xs md:text-sm lg:text-sm text-gray-700 font-medium leading-relaxed hidden sm:inline-block"
                    style={{ 
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      flexShrink: 1
                    }}
                  >
                    {currentCard.description}
                  </span>
                  <span className="text-[10px] text-gray-700 font-medium leading-relaxed sm:hidden">
                    {currentCard.description}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right side - CTA Button */}
          <div className="flex-shrink-0">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.button
                key={`cta-${currentIndex}`}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                onClick={currentCard.action}
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="group relative bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-3 sm:px-4 md:px-5 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3 text-xs sm:text-sm md:text-sm lg:text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#00d690] focus:ring-offset-2 focus:ring-offset-white overflow-hidden btn-animate-bounce"
                aria-label={`${currentCard.cta} - ${currentCard.headline}`}
              >
                {/* Shine effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
                
                {/* Button content */}
                <span className="relative z-10 flex items-center gap-1 sm:gap-2">
                  <span className="hidden sm:inline">{currentCard.cta}</span>
                  <span className="sm:hidden">{currentCard.ctaMobile || currentCard.cta}</span>
                  <Icon 
                    name="ArrowRight" 
                    size={12}
                    className="sm:w-4 sm:h-4 md:w-4 md:h-4 lg:w-4 lg:h-4 transition-transform duration-300 group-hover:translate-x-0.5" 
                  />
                </span>
              </motion.button>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RotatingTicker;

